import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table'; 
import TableBody from '@mui/material/TableBody'; 
import TableCell from '@mui/material/TableCell'; 
import TableContainer from '@mui/material/TableContainer'; 
import TableHead from '@mui/material/TableHead'; 
import TableRow from '@mui/material/TableRow'; 
import Button from '@mui/material/Button'; 
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import API_BASE_URL from '../config';
import Modal from '@mui/material/Modal';

export default function Evidences() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const videoRef = useRef(null);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [payloadData, setPayloadData] = useState(null);

  const showPayload = React.useCallback((data) => {
    setPayloadData(data);
    setPayloadOpen(true);
  }, []);

  const handleVideoClose = () => {
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        // unset src to free memory for large files
        videoRef.current.src = '';
      }
    } catch (e) {
      // ignore
    }
    setVideoOpen(false);
    setVideoUrl('');
  };

  const formatSLTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const offsetDate = new Date(date.getTime() + (330 - date.getTimezoneOffset()) * 60000);
    return offsetDate.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }) + ' (GMT+5:30)';
  };

  useEffect(() => {
    const fetchEvidences = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/evidences/full`, {
          headers: {
            'Accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Failed to fetch evidences');
        const data = await res.json();
        // endpoint likely returns array
        setItems(Array.isArray(data) ? data : (data.evidences || data.items || []));
      } catch (e) {
        setError(e.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchEvidences();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(it => {
      return (
        String(it.VehicleId || '').toLowerCase().includes(q) ||
        String(it.Company_Name || '').toLowerCase().includes(q) ||
        String(it.driver_name || '').toLowerCase().includes(q) ||
  String(it.linde_response || '').toLowerCase().includes(q) ||
        String(it.cardid || '').toLowerCase().includes(q) ||
        String(it.Country_Code || '').toLowerCase().includes(q) ||
        String(it.alert_name || '').toLowerCase().includes(q)
      );
    });
  }, [items, query]);

  const handleChangePage = (event, newPage) => {
    // pagination removed - placeholder to avoid unused var changes
    return;
  };

  // react-table data/columns and instance
  const data = useMemo(() => filtered.map((it, idx) => ({
    idx: idx + 1,
    license: it.VehicleId || '',
    speed: it.Speed ? `${it.Speed} km/h` : '0 km/h',
    driver: it.driver_name || it.driver || it.swipe_drivername || '',
    device: it.Device_Id || it.device_id || it.device || '',
    alert: it.alert_name || '',
  linde_response: it.linde_response || it.lindeResponse || it.linde || '',
    ref: it.RefURL || it.RefID || it.ref_id || it.ref || it.reference,
    createdAt: (it.AlertDateTime || it.created_at || it.timestamp || it.execution_time),
    original: it,
  })), [filtered]);

  const columns = useMemo(() => [
    { Header: '#', accessor: 'idx' },
    { Header: 'License Plate', accessor: 'license' },
    { Header: 'Speed', accessor: 'speed' },
    { Header: 'Driver', accessor: 'driver' },
    { Header: 'Linde Response', accessor: 'linde_response' },
    { Header: 'Device', accessor: 'device' },
    { Header: 'Alert', accessor: 'alert' },
    { Header: 'Payload', accessor: 'payload', Cell: ({ row }) => {
        const orig = row.original?.original || {};
        const sp = orig.sent_parameters || orig.send_parameters_json || orig.sendParameters || orig.send_params || orig.payload || null;
        if (!sp) return null;
        return (
          <Button size="small" onClick={() => showPayload(sp)}>Show Payload</Button>
        );
      }
    },
    {
      Header: 'Video', accessor: 'ref', Cell: ({ value }) => {
        if (!value) return null;
        const ref = value;
        const isUrl = typeof ref === 'string' && /^(https?:)?\/\//i.test(ref);
        const src = isUrl ? ref : `${API_BASE_URL}/player/${encodeURIComponent(ref)}`;
          return (
            <IconButton
              size="small"
              onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
              aria-label="Open video in new tab"
            >
              <PlayArrowIcon />
            </IconButton>
          );
      }
    },
    { Header: 'Created At', accessor: 'createdAt' },
  ], [setVideoUrl, setVideoOpen, showPayload]);

  const tableInstance = useTable({ columns, data, initialState: { pageIndex: 0, pageSize: 10 } }, useGlobalFilter, useSortBy, usePagination);
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, setGlobalFilter, canPreviousPage, canNextPage, nextPage, previousPage, state: { pageIndex, pageSize } } = tableInstance;

  // keep react-table global filter in sync with our query state
  useEffect(() => {
    if (setGlobalFilter) setGlobalFilter(query || undefined);
  }, [query, setGlobalFilter]);

  return (
    <div>
      <Typography variant="h4" gutterBottom>Evidences</Typography>

      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <TextField label="Search" value={query} onChange={(e) => setQuery(e.target.value)} size="small" />
          </Box>

          <TableContainer component={Paper} sx={{ mt: 1, overflowX: 'auto' }}>
            <div style={{ minWidth: 640 }}>
              <Table {...getTableProps()}>
              <TableHead>
                {headerGroups.map(headerGroup => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <TableCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render('Header')}
                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <TableRow {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
              </Table>
            </div>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
              <Box>
                <Button onClick={() => previousPage()} disabled={!canPreviousPage}>Previous</Button>
                <Button onClick={() => nextPage()} disabled={!canNextPage}>Next</Button>
              </Box>
              <Box>Page {pageIndex + 1}</Box>
            </Box>
          </TableContainer>
        </>
      )}
      <Modal open={videoOpen} onClose={handleVideoClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 24, width: { xs: '95%', sm: '90%', md: '80%' }, maxHeight: '85vh', overflowY: 'auto' }}>
          <Typography variant="h6" gutterBottom>Video Player</Typography>
          {videoUrl ? (
            <video ref={videoRef} src={videoUrl} controls preload="metadata" style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          ) : (
            <Typography>No video URL available</Typography>
          )}
        </Box>
      </Modal>
      <Modal open={payloadOpen} onClose={() => setPayloadOpen(false)}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 24, width: { xs: '95%', sm: '80%', md: '70%' }, maxHeight: '85vh', overflowY: 'auto' }}>
          <Typography variant="h6" gutterBottom>Payload</Typography>
          {payloadData ? (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{typeof payloadData === 'string' ? (() => {
              try { const obj = JSON.parse(payloadData); return JSON.stringify(obj, null, 2); } catch (e) { return payloadData; }
            })() : JSON.stringify(payloadData, null, 2)}</pre>
          ) : (
            <Typography>No payload data available</Typography>
          )}
        </Box>
      </Modal>
    </div>
  );
}
